import { useEffect, useRef, useState } from 'react';
import { Square, Volume2 } from 'lucide-react';
import type { CanNTalkSession, CanNTalkTurn } from '../../shared/canntalk';
import { prepareCantonesePlayback, speakCantoneseQueue, type CantoneseSpeechItem } from './browserVoice';

/** The server chooses the voice from the session snapshot; clients never submit provider voice IDs. */
export function sessionSpeechItem(session: Pick<CanNTalkSession, 'id' | 'avatars'>, turn: CanNTalkTurn): CantoneseSpeechItem {
  const avatar = session.avatars.find(item => item.id === turn.speakerId);
  return { id: turn.id, text: turn.text, speakerId: turn.speakerId, voiceGender: avatar?.voiceGender,
    ...(avatar?.voice ? { speechRequest: { sessionId: session.id, turnId: turn.id } } : {}),
  };
}

type Props = {
  item: CantoneseSpeechItem; disabled?: boolean; label?: string; ariaLabel?: string;
  onActiveChange?: (active: boolean) => void; onSpeakerChange?: (speakerId: string | null) => void;
};

export default function AvatarVoicePreview(props: Props) {
  const [active, setActive] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  const callbacks = useRef(props); callbacks.current = props;
  const mounted = useRef(false);
  const request = props.item.speechRequest;
  const identity = request ? 'avatarId' in request ? `avatar:${request.avatarId}` : `turn:${request.sessionId}:${request.turnId}` : props.item.id;
  function stop() {
    const previous = controller.current; controller.current = null; previous?.abort();
    callbacks.current.onActiveChange?.(false); callbacks.current.onSpeakerChange?.(null);
    if (mounted.current) setActive(false);
  }
  useEffect(() => {
    mounted.current = true; setError(''); setActive(false);
    return () => {
      mounted.current = false;
      const previous = controller.current; controller.current = null; previous?.abort();
      callbacks.current.onActiveChange?.(false); callbacks.current.onSpeakerChange?.(null);
    };
  }, [identity]);
  useEffect(() => { if (props.disabled && controller.current) stop(); }, [props.disabled]);

  async function play() {
    if (controller.current || callbacks.current.disabled) return;
    const run = new AbortController(); controller.current = run;
    const item = callbacks.current.item;
    const isCurrent = () => mounted.current && controller.current === run && !run.signal.aborted;
    setActive(true); setError(''); callbacks.current.onActiveChange?.(true);
    try {
      // This call must occur in the click handler before any network await.
      if (item.speechRequest) await prepareCantonesePlayback({ signal: run.signal });
      if (!isCurrent()) return;
      await speakCantoneseQueue([item], { signal: run.signal, onSpeaker: id => { if (isCurrent()) callbacks.current.onSpeakerChange?.(id); } });
    } catch (caught) {
      if (isCurrent()) setError(caught instanceof Error ? caught.message : '未能播放聲音，請再試。Audio could not be played.');
    } finally {
      if (isCurrent()) { controller.current = null; setActive(false); callbacks.current.onActiveChange?.(false); callbacks.current.onSpeakerChange?.(null); }
    }
  }
  return <div className="cnt-avatar-voice-player">
    {active ? <button type="button" className="secondary-button" onClick={stop}><Square size={15} /> 停止播放 Stop playback</button>
      : <button type="button" className="secondary-button" aria-label={props.ariaLabel} disabled={props.disabled} onClick={() => void play()}><Volume2 size={16} /> {props.label ?? '試聽聲音 Listen'}</button>}
    {active && <span role="status">準備或播放聲音… Preparing or playing audio…</span>}
    {error && <p className="cnt-error" role="alert">{error}</p>}
  </div>;
}
